import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const NCR_TEMPLATE_API_URL = `${BASE_API_URL}/NcrTemplate`;

export interface NcrTemplateStage {
  id?: number;
  templateId?: number;
  parentStageId?: number;
  name: string;
  description?: string;
  ownerId?: number;
  targetDate?: string;
  sortOrder: number;
  children?: NcrTemplateStage[];
}

export interface NcrTemplateProperty {
  id?: number;
  templateId?: number;
  name: string;
  description?: string;
  isRequired: boolean;
  sortOrder: number;
}

export interface NcrTemplate {
  id?: number;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  stages: NcrTemplateStage[];
  properties: NcrTemplateProperty[];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
  };
};

const normalizeTemplate = (template: any): NcrTemplate => ({
  id: template?.id ?? template?.Id,
  name: template?.name ?? template?.Name ?? '',
  description: template?.description ?? template?.Description ?? '',
  isActive: Boolean(template?.isActive ?? template?.IsActive ?? true),
  createdAt: template?.createdAt ?? template?.CreatedAt,
  updatedAt: template?.updatedAt ?? template?.UpdatedAt,
  stages: Array.isArray(template?.stages ?? template?.Stages) ? (template?.stages ?? template?.Stages) : [],
  properties: Array.isArray(template?.properties ?? template?.Properties) ? (template?.properties ?? template?.Properties) : [],
});

const normalizeTemplates = (payload: any): NcrTemplate[] => {
  const list = Array.isArray(payload) ? payload : (payload?.data ?? payload?.Data ?? []);
  return Array.isArray(list) ? list.map(normalizeTemplate) : [];
};

export const getAllNcrTemplates = async (): Promise<NcrTemplate[]> => {
  try {
    const response = await axios.get(NCR_TEMPLATE_API_URL, {
      headers: getAuthHeaders(),
    });
    return normalizeTemplates(response.data);
  } catch (error: any) {
    console.error('Failed to get NCR templates:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to get NCR templates'
    );
  }
};

export const getNcrTemplateById = async (id: string | number): Promise<NcrTemplate> => {
  try {
    const response = await axios.get(`${NCR_TEMPLATE_API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
    return normalizeTemplate(response.data);
  } catch (error: any) {
    console.error(`Failed to get NCR template ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to get NCR template'
    );
  }
};

export const createNcrTemplate = async (template: NcrTemplate): Promise<NcrTemplate> => {
  try {
    const response = await axios.post(NCR_TEMPLATE_API_URL, template, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
    });
    return normalizeTemplate(response.data);
  } catch (error: any) {
    console.error('Failed to create NCR template:', error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to create NCR template'
    );
  }
};

export const updateNcrTemplate = async (id: string | number, template: NcrTemplate): Promise<NcrTemplate> => {
  try {
    const response = await axios.put(`${NCR_TEMPLATE_API_URL}/${id}`, template, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
    });
    return normalizeTemplate(response.data);
  } catch (error: any) {
    console.error(`Failed to update NCR template ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to update NCR template'
    );
  }
};

export const deleteNcrTemplate = async (id: string | number): Promise<void> => {
  try {
    await axios.delete(`${NCR_TEMPLATE_API_URL}/${id}`, {
      headers: getAuthHeaders(),
    });
  } catch (error: any) {
    console.error(`Failed to delete NCR template ${id}:`, error);
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.detail ||
      'Failed to delete NCR template'
    );
  }
};
